import { range } from 'lodash';
import React, { FunctionComponent, useState } from 'react';
import { ShoppingCartIcon, ForwardMediaIcon } from '@lyse-as/formds-react/lib/icon/common';

import { Dropdown } from 'src/components/dropdown';
import { ProductDetails } from 'src/components/product-details';
import { useSelectIsEligibleToPurchase } from 'src/hooks/use-select-is-eligible-to-purchase';
import { useSelectMaximumStbPurchaseCount } from 'src/hooks/use-select-maximum-stb-purchase-count';
import { addPurchasesToCartActionCreator } from 'src/state/cart/action-creators/add-purchases-action-creator';
import { selectItemFromCart } from 'src/state/cart/selectors';
import { OrderStep } from 'src/state/order/types';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';
import { getNumberOfUpgradeUnits, isEligibleToUpgrade } from 'src/utils/equipment-utils';
import { I18nUtils } from 'src/utils/i18n-utils';
import { i18n } from 'src/i18n';
import { withOrderStep } from 'src/hocs/with-order-step';
import { useProductsResponseQueryStateOrThrow } from 'src/hooks/use-products-query';

import { StyledContent, StyledDescription } from './purchase-page.styled';

export const PurchasePageComponent: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const maximumPurchaseCount = useSelectMaximumStbPurchaseCount();
  const purchaseAllowed = useSelectIsEligibleToPurchase();
  const { newDecoder, upgradeDecoder } = useProductsResponseQueryStateOrThrow();
  const { purchase: purchasesInCart } = useAppSelector(selectItemFromCart);
  const [selectedAmount, setSelectedAmount] = useState(Math.max(1, purchasesInCart));
  const ownedStbCount = getNumberOfUpgradeUnits(upgradeDecoder);
  const upgradeLink = isEligibleToUpgrade(upgradeDecoder)
    ? {
        productDetailsPage: OrderStep.UPGRADE_DECODER,
        text: i18n.page.productSelect.product.linkText,
      }
    : undefined;

  const renderContent = () => (
    <StyledContent>
      <StyledDescription variant="paragraph2" component="p">
        {upgradeDecoder
          ? I18nUtils.message(i18n.page.productSelect.product.descriptionOwnedDecoders, {
              currentDecoderCount: ownedStbCount,
            })
          : i18n.page.productSelect.product.description}
      </StyledDescription>
      {purchaseAllowed && (
        <Dropdown
          label={i18n.page.productSelect.product.selectDecoderCount}
          onChange={(value) => setSelectedAmount(parseInt(value, 10))}
          options={range(1, maximumPurchaseCount + 1).map((idx) => ({ value: idx, label: idx.toString() }))}
          value={selectedAmount}
        />
      )}
    </StyledContent>
  );

  const getButtonAddProps = () => ({
    children: (
      <>
        <ShoppingCartIcon /> {i18n.page.productSelect.product.addToCart}
      </>
    ),
    disabled: !purchaseAllowed,
  });

  const getButtonUpdateProps = () => {
    const disabled = selectedAmount === purchasesInCart;

    return {
      children: (
        <>
          <ForwardMediaIcon /> {i18n.page.productSelect.product.updateCart}
        </>
      ),
      disabled,
    };
  };

  return (
    <ProductDetails
      title={i18n.page.productSelect.product.title}
      content={renderContent()}
      mainPrice={
        newDecoder && {
          price: newDecoder.monthlyPrice,
          text: i18n.page.productSelect.product.mainPriceText,
        }
      }
      secondaryPrice={
        newDecoder && {
          price: newDecoder.onceOnlyPrice,
          text: i18n.page.productSelect.product.secondaryPriceText,
        }
      }
      buttonProps={{
        ...(purchasesInCart === 0 ? getButtonAddProps() : getButtonUpdateProps()),
        onClick: () => dispatch(addPurchasesToCartActionCreator(selectedAmount)),
      }}
      link={upgradeLink}
      showQuantityAlert={!purchaseAllowed}
    />
  );
};

export const PurchasePage = withOrderStep(OrderStep.PURCHASE_DECODER)(PurchasePageComponent);
